import { useState } from "react";
import { Newspaper, Mail, Loader2, Check } from "lucide-react";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/ga4";

/**
 * DigestSubscribeForm — formulário inline para inscrição no digest
 * semanal do JurisTube. Usado em:
 *   - <DigestSubscribeModal /> (popup automático em /noticias-juridicas)
 *   - rodapé das páginas de notícias (variant="banner")
 *   - página dedicada /newsletter (variant="page")
 *
 * A inscrição é canalizada pelo endpoint genérico
 * `/api/newsletter/subscribe` (double opt-in via ZeptoMail). Todo
 * assinante confirmado passa a receber automaticamente o digest
 * semanal todas as sextas-feiras às 12h BRT — não há toggle separado.
 */
export default function DigestSubscribeForm({
  variant = "card",
  source = "legal-news-digest",
  testidPrefix = "digest-subscribe",
}) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | sent | already | error
  const [errorMsg, setErrorMsg] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (state === "loading") return;
    const clean = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(clean)) {
      setState("error");
      setErrorMsg("Digite um e-mail válido.");
      return;
    }
    setState("loading");
    setErrorMsg("");
    try {
      const r = await api.subscribe(clean, source);
      if (r?.already_subscribed) {
        setState("already");
        trackEvent("digest_subscribe", { source, outcome: "already_subscribed" });
      } else {
        setState("sent");
        trackEvent("digest_subscribe", { source, outcome: "pending_confirmation" });
      }
    } catch (err) {
      setState("error");
      setErrorMsg(err?.response?.data?.detail || "Não foi possível inscrever. Tente novamente.");
      trackEvent("digest_subscribe", { source, outcome: "error" });
    }
  };

  const isCard = variant === "card";
  const isBanner = variant === "banner";
  const isPage = variant === "page";

  if (state === "sent" || state === "already") {
    return (
      <div
        className={`rounded-xl border ${isBanner ? "border-emerald-500/40 bg-emerald-500/5 px-4 py-3" : "border-emerald-500/40 bg-emerald-500/10 p-5"}`}
        data-testid={`${testidPrefix}-success`}
      >
        <div className="flex items-start gap-3">
          <Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-emerald-100">
              {state === "already" ? "Você já está inscrito(a)!" : "Quase lá — confirme seu e-mail!"}
            </div>
            <div className="text-xs text-emerald-200/80 mt-1 leading-relaxed">
              {state === "already"
                ? "Continue ligado: você já está na lista do boletim do JurisTube."
                : "Enviamos um link de confirmação para o seu e-mail. Após clicar, você passa a receber o boletim com as novidades do site."}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className={
        isPage
          ? "space-y-3"
          : isBanner
            ? "flex flex-col sm:flex-row items-stretch gap-2"
            : "space-y-3"
      }
      data-testid={`${testidPrefix}-form`}
      noValidate
    >
      {(isCard || isPage) && (
        <div className="flex items-center gap-2 mb-1">
          <Newspaper className="w-4 h-4 text-sky-400" />
          <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-sky-300/90">
            Boletim do JurisTube
          </span>
        </div>
      )}
      {(isCard || isPage) && (
        <div>
          <div className="font-serif text-lg text-white font-bold leading-snug">
            {isPage
              ? "Receba automaticamente as novidades do site"
              : "Blog, vídeos, decisões e normas — direto no seu e-mail"}
          </div>
          <p className="text-xs text-slate-400 leading-relaxed mt-1.5">
            Mandamos para você os <strong>posts mais recentes do blog</strong>, os{" "}
            <strong>novos vídeos</strong> do acervo, as <strong>principais decisões</strong> dos
            tribunais superiores e as <strong>normas novas</strong> publicadas no DOU. Curadoria
            automática, leitura de 5 minutos.
          </p>
        </div>
      )}
      <div className={isBanner ? "flex flex-col sm:flex-row items-stretch gap-2 w-full" : "flex flex-col sm:flex-row items-stretch gap-2"}>
        <div className="relative flex-1">
          <Mail className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            placeholder="seu@email.com.br"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (state === "error") setState("idle"); }}
            className="w-full bg-slate-950/70 border border-slate-700 rounded-full pl-10 pr-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-sky-500/60 focus:outline-none transition"
            data-testid={`${testidPrefix}-email-input`}
            disabled={state === "loading"}
          />
        </div>
        <button
          type="submit"
          disabled={state === "loading"}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-slate-950 text-sm font-semibold transition whitespace-nowrap"
          data-testid={`${testidPrefix}-submit`}
        >
          {state === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Newspaper className="w-4 h-4" />}
          Receber digest
        </button>
      </div>
      {state === "error" && errorMsg && (
        <div className="text-xs text-rose-300" data-testid={`${testidPrefix}-error`}>{errorMsg}</div>
      )}
      {(isCard || isPage) && (
        <p className="text-[10px] text-slate-500 leading-snug">
          Confirmação por e-mail (double opt-in). Descadastro com 1 clique a qualquer momento.
          Nenhum dado é compartilhado com terceiros.
        </p>
      )}
    </form>
  );
}
